// AntifraudMethods.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  useMediaQuery, useTheme, Alert, Snackbar, Stack
} from "@mui/material";
import { useAntifraudMethods } from "../../../hooks/useAntifraudMethods";
import AntifraudMethodTable from "../../../components/AntifraudMethodTable";
import AddAntifraudMethodForm from "../../../components/AddAntifraudMethodForm";
import { usePageContext } from "../../../context/PageContext";
import { useTranslation } from "react-i18next";

const AntifraudMethods = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data, error, refetch, updateMutation, updateStatusMutation, deleteMutation,
    isLoading, isFetching, isRefetching
  } = useAntifraudMethods(currentPage, pageSize, search);

  useEffect(() => { if (error) setErrorMessage(t("antifraudPage.loadError")); else setErrorMessage(null); }, [error, t]);
  useEffect(() => { const timeoutId = setTimeout(() => setSearch(searchInput.trim()), 350); return () => clearTimeout(timeoutId); }, [searchInput]);

  const handleEdit = (row) => { setSelected({ id: row.id }); setErrorMessage(null); };
  const handleDelete = (id) => { setConfirmDelete(id); setErrorMessage(null); };
  const confirmDeleteAction = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setSnack({ open: true, message: t("antifraudPage.deleted"), severity: "success" });
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(err?.response?.data?.message || t("antifraudPage.deleteFailed"));
        setSnack({ open: true, message: t("antifraudPage.deleteFailed"), severity: "error" });
      },
      onSettled: () => setIsProcessing(false),
    });
  };
  const handleToggleStatus = (row) => {
    const newStatus = row.status === 1 ? 0 : 1;
    updateStatusMutation.mutate(
      { id: row.id, status: newStatus },
      {
        onSuccess: () => setSnack({ open: true, message: newStatus ? t("antifraudPage.methodActivated") : t("antifraudPage.methodDeactivated"), severity: "success" }),
        onError: () => setSnack({ open: true, message: t("antifraudPage.statusUpdateFailed"), severity: "error" }),
      }
    );
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id))
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? t("antifraudPage.bulkDeleteError")
          : t("antifraudPage.bulkDeletePartial", { failed: failed.length, total: ids.length })
      );
      setSnack({ open: true, message: t("antifraudPage.someDeletesFailed"), severity: "error" });
    } else {
      setSnack({ open: true, message: t("antifraudPage.bulkDeleteSuccess"), severity: "success" });
    }
    refetch();
    setIsProcessing(false);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newSize) => setPageSize(newSize);
  const handleCloseForm = () => { setSelected(null); setErrorMessage(null); };

  const busy = isLoading || isFetching || isRefetching || isProcessing;
  const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 };

  const headerRight = useMemo(() => (
    <Stack direction={isMobile ? "column" : "row"} spacing={1} alignItems="center">
      <Button variant="contained" color="primary" fullWidth={isMobile} onClick={() => setSelected({})}>
        {t("antifraudPage.addAntifraud")}
      </Button>
    </Stack>
  ), [isMobile, t]);

  return (
    <Box sx={{ pt: 1 }}>
      <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", mb: 2, gap: isMobile ? 2 : 0 }}>
        <Typography variant="h4" gutterBottom>{t("antifraudPage.heading")}</Typography>
        {headerRight}
      </Box>

      {errorMessage && <Box sx={{ mb: 2 }}><Alert severity="error">{errorMessage}</Alert></Box>}

      {selected ? (
        <AddAntifraudMethodForm
          method={selected}
          isFetching={busy}
          onClose={handleCloseForm}
          onSubmit={(payload) => {
            setIsProcessing(true);
            updateMutation.mutate(payload, {
              onSuccess: () => {
                setSnack({ open: true, message: t("antifraudPage.saved"), severity: "success" });
                refetch();
                handleCloseForm();
              },
              onError: (err) => {
                setErrorMessage(err?.response?.data?.message || t("antifraudPage.saveError"));
                setSnack({ open: true, message: t("antifraudPage.saveFailed"), severity: "error" });
              },
              onSettled: () => setIsProcessing(false),
            });
          }}
        />
      ) : (
        <AntifraudMethodTable
          methods={data?.data || []}
          rowCount={pagination.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={(term) => setSearchInput(term)}
          isLoading={busy}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDeleteSelected={handleDeleteSelected}
          onToggleStatus={handleToggleStatus}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("antifraudPage.deleteDialogTitle")}</DialogTitle>
        <DialogContent><Typography>{isProcessing ? t("antifraudPage.processing") : t("antifraudPage.deleteOneConfirm")}</Typography></DialogContent>
        <DialogActions>
          {!isProcessing && <>
            <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
            <Button color="error" onClick={confirmDeleteAction}>{t("shared.delete")}</Button>
          </>}
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })} message={snack.message} />
    </Box>
  );
};

export default AntifraudMethods;
